/**
 * Developer "Docs" tab: canonical request/response examples for one endpoint.
 *
 * Everything is derived from the endpoint's `models.yaml` spec (`model.inputs`,
 * `model.path`, `model.model_id`) so the examples stay in sync with the backend
 * without any hand-maintained per-endpoint documentation.
 */
import { ReactNode } from 'react';
import { APIGEN_API_KEY } from '../../utils/api';
import type { ApiEndpointModel, ApiInputSpec } from '../types';
import { CopyActionButton } from './apiSnippets';
import { apigenOrigin, buildCurl, buildGetCurl } from './curl';

/** Shared request-envelope fields every endpoint accepts (from `CallMeta`). */
const META_FIELDS: { name: string; type: string; description: string }[] = [
  { name: 'scan_id', type: 'string', description: 'Scan / inspection id' },
  { name: 'organization_id', type: 'string', description: 'Organization id' },
  { name: 'job_id', type: 'string', description: 'Upstream job id (correlate spend in Statistics)' },
  { name: 'view', type: 'string', description: 'View / POV (e.g. left, right, front, rear)' },
  { name: 'player', type: 'string', description: 'Player email (who initiated the request)' },
  { name: 'dvp', type: 'string', description: 'DVP identifier' },
  {
    name: 'callback_url',
    type: 'string',
    description:
      'URL to POST the finished result to when an async (wait=false) job completes.',
  },
];

/** A representative placeholder value for one endpoint input. */
function placeholderFor(spec: ApiInputSpec): unknown {
  switch (spec.type) {
    case 'image':
      return 'https://example.com/input.png';
    case 'mask':
      return 'https://example.com/mask.png';
    case 'images':
      return ['https://example.com/reference-1.png'];
    case 'number':
      return spec.name === 'seed' ? 42 : 0;
    case 'select':
      return spec.default || spec.options?.[0] || 'value';
    case 'text':
      if (spec.name === 'prompt') return 'Describe the edit you want, e.g. "remove the cone".';
      if (spec.name === 'aspect_ratio') return '4:3';
      if (spec.name === 'size') return '1024x1536';
      return 'value';
    default:
      return 'value';
  }
}

/** Build an example request body from the endpoint's input spec. */
function buildExampleBody(model: ApiEndpointModel, includeMeta: boolean): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const spec of model.inputs) {
    // Show required inputs always; include prompt even if optional (common).
    if (spec.required || spec.name === 'prompt') {
      body[spec.name] = placeholderFor(spec);
    }
  }
  if (includeMeta) {
    body.scan_id = 'fe264bea-a763-41cc-98c2-06246bfa093f';
    body.job_id = 'merch-scan-001';
    body.callback_url = 'https://your-service.example.com/webhooks/genapi';
  }
  return body;
}

function exampleModelId(model: ApiEndpointModel): string {
  return model.model_id || model.endpoint || model.key;
}

/** Terminal (success) response returned by the synchronous path. */
function syncResponseExample(model: ApiEndpointModel): Record<string, unknown> {
  return {
    record_id: '4f9c1e2a-8d3b-4a1f-9c7e-2b6d0a1f3e5c',
    status: 'success',
    model_type: model.key,
    model_id: exampleModelId(model),
    provider: model.active_provider || model.provider,
    output_image: 'iVBORw0KGgoAAAANS... (base64 PNG, omitted)',
    output_url: `${apigenOrigin().replace(/\/apigen$/, '')}/s3/output.png`,
    cost_usd: 0.04,
    elapsed_ms: 3200,
    error: null,
  };
}

/** Immediate response returned by the async (wait=false) kick-off. */
function pendingResponseExample(model: ApiEndpointModel): Record<string, unknown> {
  return {
    record_id: '4f9c1e2a-8d3b-4a1f-9c7e-2b6d0a1f3e5c',
    status: 'pending',
    model_type: model.key,
    model_id: exampleModelId(model),
    provider: null,
    output_image: null,
    output_url: null,
    cost_usd: null,
    elapsed_ms: null,
    error: null,
  };
}

/**
 * The finished record — both the shape returned by `GET /v1/calls/{id}` (poll)
 * and the payload POSTed to `callback_url`.
 */
function finishedRecordExample(model: ApiEndpointModel): Record<string, unknown> {
  return {
    id: '4f9c1e2a-8d3b-4a1f-9c7e-2b6d0a1f3e5c',
    created_at: '2026-01-15T13:06:47.000Z',
    scan_id: 'fe264bea-a763-41cc-98c2-06246bfa093f',
    organization_id: 'HDaSz4ke4clyq6dg1maA',
    job_id: 'merch-scan-001',
    view: null,
    player: null,
    dvp: null,
    model_type: model.key,
    model_id: exampleModelId(model),
    provider: model.provider,
    prompt: model.inputs.some((i) => i.name === 'prompt') ? 'remove the cone' : null,
    seed: null,
    params: {},
    input_refs: { image: 'https://.../input.png' },
    output_ref: 'https://.../output.png',
    cost_usd: 0.04,
    duration_seconds: 3.2,
    status: 'success',
    error: null,
  };
}

function json(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

export function ApiDocsTab({ model }: { model: ApiEndpointModel }) {
  const syncBody = buildExampleBody(model, false);
  const asyncBody = buildExampleBody(model, true);
  const syncCurl = buildCurl(model.path, syncBody);
  const asyncCurl = buildCurl(`${model.path}?wait=false`, asyncBody);
  const pollCurl = buildGetCurl('/v1/calls/4f9c1e2a-8d3b-4a1f-9c7e-2b6d0a1f3e5c');

  return (
    <div className="max-w-4xl space-y-8">
      {/* Overview / auth */}
      <section>
        <SectionTitle>Overview</SectionTitle>
        <p className="text-sm text-slate-600 leading-relaxed">
          <code className="font-mono text-slate-800">{model.label}</code> is a JSON HTTP endpoint.
          Send a <span className="font-semibold">POST</span> to{' '}
          <code className="font-mono text-indigo-600">{model.path}</code>. Image and mask inputs
          accept either an <code className="font-mono">http(s)</code> URL or a base64 string.
        </p>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <MetaRow label="Base URL">
            <code className="font-mono text-slate-800 break-all">{apigenOrigin()}</code>
          </MetaRow>
          <MetaRow label="Method / path">
            <code className="font-mono text-slate-800">POST {model.path}</code>
          </MetaRow>
          <MetaRow label="Auth header">
            <code className="font-mono text-slate-800">
              {APIGEN_API_KEY ? 'X-API-Key: <your key>' : 'X-API-Key (when configured)'}
            </code>
          </MetaRow>
          <MetaRow label="Content-Type">
            <code className="font-mono text-slate-800">application/json</code>
          </MetaRow>
        </dl>
      </section>

      {/* Request fields */}
      <section>
        <SectionTitle>Request body</SectionTitle>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <Th>Field</Th>
                <Th>Type</Th>
                <Th>Required</Th>
                <Th>Description</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {model.inputs.map((spec) => (
                <tr key={spec.name}>
                  <Td>
                    <code className="font-mono text-indigo-600">{spec.name}</code>
                  </Td>
                  <Td>
                    <code className="font-mono text-slate-500">{spec.type}</code>
                  </Td>
                  <Td>{spec.required ? <Required /> : <span className="text-slate-400">optional</span>}</Td>
                  <Td>{spec.label}</Td>
                </tr>
              ))}
              {META_FIELDS.map((f) => (
                <tr key={f.name} className="bg-slate-50/40">
                  <Td>
                    <code className="font-mono text-slate-600">{f.name}</code>
                  </Td>
                  <Td>
                    <code className="font-mono text-slate-500">{f.type}</code>
                  </Td>
                  <Td>
                    <span className="text-slate-400">optional</span>
                  </Td>
                  <Td>{f.description}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">
          The <code className="font-mono">wait</code> query flag controls sync vs async (see below).
          Shared metadata fields are optional but recommended so calls can be traced and their spend
          rolled up per <code className="font-mono">job_id</code>.
        </p>
      </section>

      {/* Synchronous */}
      <section>
        <SectionTitle>Synchronous (default)</SectionTitle>
        <p className="text-sm text-slate-600 leading-relaxed">
          The request blocks until the provider finishes and returns the full result inline
          (base64 <code className="font-mono">output_image</code> plus an{' '}
          <code className="font-mono">output_url</code>). Good for quick calls; a very slow
          generation can be cut off by an intermediate proxy timeout — use async mode for those.
        </p>
        <Example title="Request" code={syncCurl} />
        <Example title="Request body" code={json(syncBody)} lang="json" />
        <Example title="Response (200)" code={json(syncResponseExample(model))} lang="json" />
      </section>

      {/* Asynchronous */}
      <section>
        <SectionTitle>Asynchronous (wait=false)</SectionTitle>
        <p className="text-sm text-slate-600 leading-relaxed">
          Add <code className="font-mono">?wait=false</code>. The service writes a{' '}
          <code className="font-mono">pending</code> record and returns its{' '}
          <code className="font-mono">record_id</code> immediately, then runs the generation in the
          background. Retrieve the result by <span className="font-semibold">polling</span> or by
          supplying a <code className="font-mono">callback_url</code> (or both).
        </p>
        <Example title="Kick off" code={asyncCurl} />
        <Example title="Immediate response (200)" code={json(pendingResponseExample(model))} lang="json" />

        <h4 className="mt-5 mb-1 text-sm font-semibold text-slate-700">Option A — poll</h4>
        <p className="text-sm text-slate-600 leading-relaxed">
          Poll <code className="font-mono">GET /v1/calls/&#123;record_id&#125;</code> until{' '}
          <code className="font-mono">status</code> is no longer{' '}
          <code className="font-mono">pending</code> (i.e. <code className="font-mono">success</code>{' '}
          or <code className="font-mono">error</code>).
        </p>
        <Example title="Poll" code={pollCurl} />
        <Example title="Poll response (200)" code={json(finishedRecordExample(model))} lang="json" />

        <h4 className="mt-5 mb-1 text-sm font-semibold text-slate-700">Option B — callback</h4>
        <p className="text-sm text-slate-600 leading-relaxed">
          If you include <code className="font-mono">callback_url</code> in the request body, the
          service POSTs the finished record to that URL when the job completes (on both success and
          error). This mirrors the merch (lightning) and interior services. The callback is
          best-effort and authenticated with the{' '}
          <code className="font-mono">uveye-api-key</code> header when the service is configured with
          a UVCAMP key.
        </p>
        <Example title="Callback payload (POST to your callback_url)" code={json(finishedRecordExample(model))} lang="json" />
        <p className="mt-2 text-[11px] text-slate-400">
          Headers on the callback: <code className="font-mono">Content-Type: application/json</code>
          {' '}and <code className="font-mono">uveye-api-key: &lt;UVCAMP_KEY&gt;</code> (when set).
          Return any 2xx to acknowledge; non-2xx and failures are logged but not retried.
        </p>
      </section>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h3 className="text-sm font-semibold text-slate-900 mb-2">{children}</h3>;
}

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col py-1">
      <dt className="text-[11px] uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="text-left font-medium px-3 py-2 whitespace-nowrap">{children}</th>;
}

function Td({ children }: { children: ReactNode }) {
  return <td className="px-3 py-2 align-top text-slate-600">{children}</td>;
}

function Required() {
  return (
    <span className="inline-flex items-center text-[11px] font-medium text-rose-600 bg-rose-50 rounded px-1.5 py-0.5">
      required
    </span>
  );
}

function Example({ title, code, lang }: { title: string; code: string; lang?: 'json' | 'shell' }) {
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wide text-slate-400">
          {title}
          {lang ? <span className="ml-1 text-slate-300">· {lang}</span> : null}
        </span>
        <CopyActionButton value={code} icon="copy">
          Copy
        </CopyActionButton>
      </div>
      <pre className="overflow-x-auto rounded-lg bg-slate-900 text-slate-100 text-xs leading-relaxed p-3 font-mono">
        <code>{code}</code>
      </pre>
    </div>
  );
}
